import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../../../router/all_routes";
import CollapseHeader from "../../../../../core/common/collapse-header";
import { checkPermission } from "../../../../../core/permissons/CheckPermissons";
import apiDefinitions from "../../../../../core/api/apiDefinitions";

const route = all_routes;
const PAGE_SIZE = 15; // ✅ departments per page

interface Employee {
  id?: number;
  name: string;
  empNo: string | null;
  designation: string;
  email: string;
  phone: string;
  status: string | null;
  personType: string;
}

interface Department {
  id: number;
  name: string;
}

interface Group {
  id: number;
  name: string;
  description: string;
  status: string;
  createdAt: string;
}

const AddUsers: React.FC = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalSearchTerm, setModalSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState<Employee | null>(null);

  const [groupName, setGroupName] = useState<string>("");

  const [driverData, setDriverData] = useState({
    licenseType: "",
    licenseNumber: "",
    expireDate: "",
  });

  const [deptHeadData, setDeptHeadData] = useState<Department[]>([]);
  const [availableDepartments, setAvailableDepartments] = useState<Department[]>([]);
  const [availableGroups, setAvailableGroups] = useState<Group[]>([]);
  const [modalError, setModalError] = useState<string | null>(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<Employee[]>([]);

  // 🔒 Role restriction states
  const [currentUserGroup, setCurrentUserGroup] = useState<string>("");
  const [allowedGroupNames, setAllowedGroupNames] = useState<string[]>([]);

  // 🔁 Department pagination states
  const [deptPage, setDeptPage] = useState<number>(1);
  const [deptTotal, setDeptTotal] = useState<number>(0);
  const [deptSearch, setDeptSearch] = useState<string>("");

  const navigate = useNavigate();

  const navItems1 = [
    {
      label: "View Users",
      icon: "ti ti-layout-dashboard",
      link: route.ViewUsers,
      permission: "ROLE_GET_USERS",
    },
    {
      label: "Add Users",
      icon: "ti ti-plus",
      link: route.AddUsers,
      permission: "ROLE_ADD_USERS",
    },
    {
      label: "Add Key Approver",
      icon: "ti ti-key",
      link: route.addkeyapprover,
      permission: "ROLE_ASSIGN_APPROVER",
    },
    {
      label: "Deactivate Key Approver",
      icon: "ti ti-x",
      link: route.deactivatekeyapprover,
      permission: "ROLE_ASSIGN_APPROVER",
    },
  ];

  // ---------- Helpers for role restrictions ----------
  const normalizeGroup = (g?: string | null) =>
    (g || "").replace("_K", "").trim().toUpperCase();

  const getCurrentUserGroup = (): string => {
    const g = localStorage.getItem("group") || sessionStorage.getItem("group");
    return normalizeGroup(g);
  };

  // Dept Head -> can assign only USER
  // Others    -> can assign only HR_MANAGER or SERVICE_MANAGER
  const getAllowedGroups = (currentGroup: string): string[] => {
    if (currentGroup === "DEPARTMENT_HEAD") return ["USER"];
    return ["HR_MANAGER", "SERVICE_MANAGER", "USER", "DRIVER", "MINOR_STAFF", "DEPARTMENT_HEAD", "SECURITY"];
  };
  // ---------------------------------------------------

  // Fetch employees and groups on component mount
  const fetchData = useCallback(async () => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    if (!token) {
      setError("No authentication token found. Please log in.");
      setLoading(false);
      navigate(route.login);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const empResponse = await apiDefinitions.getEmployees();
      if (empResponse.data.status === "S1000") {
        const sortedEmployees = empResponse.data.data.sort(
          (a: Employee, b: Employee) => {
            if (a.status === "ACTIVE" && b.status !== "ACTIVE") return -1;
            if (a.status !== "ACTIVE" && b.status === "ACTIVE") return 1;
            return 0;
          }
        );
        setEmployees(sortedEmployees);
      } else {
        setError(empResponse.data.statusDescription || "Failed to fetch employees");
      }

      const groupResponse = await apiDefinitions.getAllGroups();
      if (groupResponse.data.status === "S1000") {
        setAvailableGroups(groupResponse.data.data || []);
      } else {
        setError(groupResponse.data.statusDescription || "Failed to fetch groups");
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        setError("Unauthorized access. Your session may have expired. Please log in again.");
        localStorage.removeItem("token");
        sessionStorage.removeItem("token");
        navigate(route.login);
      } else {
        setError(err.response?.data?.statusDescription || "Error fetching data");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // init data
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // init current user group + allowed groups
  useEffect(() => {
    const g = getCurrentUserGroup();
    setCurrentUserGroup(g);
    setAllowedGroupNames(getAllowedGroups(g));
  }, []);

  // Ensure selected group remains allowed if allowed list changes
  useEffect(() => {
    if (groupName && !allowedGroupNames.includes(groupName)) {
      setGroupName("");
    }
  }, [allowedGroupNames, groupName]);

  // 🔁 Fetch departments (paginated + search) when modal is open and relevant
  useEffect(() => {
    if (modalOpen && (groupName === "USER" || groupName === "DEPARTMENT_HEAD")) {
      const fetchDepartments = async () => {
        const token = localStorage.getItem("token") || sessionStorage.getItem("token");
        if (!token) {
          setModalError("No authentication token found. Please log in.");
          navigate(route.login);
          return;
        }

        try {
          setModalLoading(true);
          setModalError(null);

          const payload = {
            filterType: deptSearch ? "NAME" : null,
            filterValue: deptSearch || null,
            pageSize: PAGE_SIZE,
          };

          const response = await apiDefinitions.getDepartments(deptPage, payload);
          if (response.data.status === "S1000") {
            const departments = Array.isArray(response.data.data?.departments)
              ? response.data.data.departments
              : [];
            setAvailableDepartments(departments);
            setDeptTotal(response.data.data?.total ?? 0);
            if (departments.length === 0) {
              setModalError("No departments found.");
            }
          } else {
            setModalError(response.data.statusDescription || "Failed to fetch departments");
          }
        } catch (err: any) {
          if (err.response?.status === 401) {
            setModalError("Unauthorized access. Your session may have expired. Please log in again.");
            localStorage.removeItem("token");
            sessionStorage.removeItem("token");
            navigate(route.login);
          } else {
            setModalError(err.response?.data?.statusDescription || "Error fetching departments");
          }
        } finally {
          setModalLoading(false);
        }
      };

      fetchDepartments();
    }
  }, [modalOpen, groupName, currentUserGroup, deptPage, deptSearch, navigate]);

  // Handle clicking an employee name to open modal
  const handleNameClick = useCallback(
    async (employee: Employee) => {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      if (!token) {
        setModalError("No authentication token found. Please log in.");
        navigate(route.login);
        return;
      }

      try {
        setModalLoading(true);
        setModalError(null);
        setSelectedUser(employee);
        setModalOpen(true);
        setModalSearchTerm("");
        setAvailableUsers([]);
        setDriverData({ licenseType: "", licenseNumber: "", expireDate: "" });
        setDeptHeadData([]);
        setDeptPage(1);      // reset dept pagination when opening
        setDeptSearch("");   // reset dept search when opening

        // Default the group to the first allowed for UX
        const firstAllowed = allowedGroupNames[0] || "";
        setGroupName(firstAllowed);
      } catch (err: any) {
        if (err.response?.status === 401) {
          setModalError("Unauthorized access. Your session may have expired. Please log in again.");
          localStorage.removeItem("token");
          sessionStorage.removeItem("token");
          navigate(route.login);
        } else if (err.response?.status === 404) {
          setModalError(null);
        } else {
          setModalError(err.response?.data?.statusDescription || "Error preparing role assignment.");
        }
      } finally {
        setModalLoading(false);
      }
    },
    [navigate, allowedGroupNames]
  );

  // Modal user search (by EmpNo)
  const handleModalSearch = async () => {
    if (!modalSearchTerm) {
      setModalError("Please enter an employee number.");
      return;
    }
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    if (!token) {
      setModalError("No authentication token found. Please log in.");
      navigate(route.login);
      return;
    }

    try {
      setModalLoading(true);
      setModalError(null);
      const response = await apiDefinitions.getEmployees();
      if (response.data.status === "S1000") {
        const filteredUsers = response.data.data.filter(
          (user: Employee) => user.empNo && user.empNo.toString().includes(modalSearchTerm)
        );
        setAvailableUsers(filteredUsers);
        if (filteredUsers.length === 0) {
          setModalError("No users found with the provided employee number.");
        }
      } else {
        setModalError(response.data.statusDescription || "Failed to fetch users.");
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        setModalError("Unauthorized access. Your session may have expired. Please log in again.");
        localStorage.removeItem("token");
        sessionStorage.removeItem("token");
        navigate(route.login);
      } else {
        setModalError(err.response?.data?.statusDescription || "Error searching users.");
      }
    } finally {
      setModalLoading(false);
    }
  };

  const handleSelectUser = (user: Employee) => {
    setSelectedUser(user);
    setModalError(null);
    const firstAllowed = allowedGroupNames[0] || "";
    setGroupName(firstAllowed);
    setDriverData({ licenseType: "", licenseNumber: "", expireDate: "" });
    setDeptHeadData([]);
    setDeptPage(1);
    setDeptSearch("");
  };

  const handleClearSelection = () => {
    setSelectedUser(null);
    setGroupName("");
    setDriverData({ licenseType: "", licenseNumber: "", expireDate: "" });
    setDeptHeadData([]);
    setModalError(null);
    setDeptPage(1);
    setDeptSearch("");
  };

  // Submit role assignment
  const handleAddUserRole = async () => {
    if (!selectedUser) {
      setModalError("Please select a user.");
      return;
    }
    if (!groupName) {
      setModalError("Please select a group.");
      return;
    }

    // 🔒 Enforce allowed groups for this current user
    if (!allowedGroupNames.includes(groupName)) {
      setModalError(
        currentUserGroup === "DEPARTMENT_HEAD"
          ? "You can only assign the USER role."
          : "You can only assign HR_MANAGER or SERVICE_MANAGER roles."
      );
      return;
    }

    // Field validations based on chosen group
    if (
      groupName === "DRIVER" &&
      (!driverData.licenseType.trim() ||
        !driverData.licenseNumber.trim() ||
        !driverData.expireDate)
    ) {
      setModalError("Please provide valid license type, number, and expiration date for driver role.");
      return;
    }
    if (groupName === "DRIVER" && driverData.licenseNumber.length < 4) {
      setModalError("License number must be at least 4 characters long.");
      return;
    }
    // Dept selection required only for USER role (Dept Head path)
    if (groupName === "USER" && deptHeadData.length === 0) {
      setModalError("Please select a department for this role.");
      return;
    }

    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    if (!token) {
      setModalError("No authentication token found. Please log in.");
      navigate(route.login);
      return;
    }

    try {
      setModalLoading(true);
      setModalError(null);

      const payload: any = {
        empNo: selectedUser.empNo?.toString(),
        name: selectedUser.name,
        email: selectedUser.email,
        phone: selectedUser.phone,
        group: `${groupName}_K`,
      };

      if (groupName === "DRIVER") {
          payload.licenseType = driverData.licenseType.trim();
          payload.licenseNumber = driverData.licenseNumber.trim();
          payload.expireDate = driverData.expireDate;
        } else if (["USER", "DEPARTMENT_HEAD"].includes(groupName)) {
          if (deptHeadData.length === 0) {
            setModalError("Please select a department for this role.");
            return;
          }

          const deptName = deptHeadData[0]?.name;
          if (!deptName || !availableDepartments.some((d) => d.name === deptName)) {
            setModalError("Selected department is invalid or not found.");
            return;
          }
          payload.departmentName = deptName;
        }
      const response = await apiDefinitions.addUser(payload);
      if (response.data.status === "S1000") {
        setSuccessMessage("User role added/updated successfully!");
        setModalOpen(false);
        setSelectedUser(null);
        setGroupName("");
        setDriverData({ licenseType: "", licenseNumber: "", expireDate: "" });
        setDeptHeadData([]);
        setModalSearchTerm("");
        setAvailableUsers([]);
        setDeptPage(1);
        setDeptSearch("");
        setTimeout(() => setSuccessMessage(null), 3000);
        await fetchData();
      } else {
        setModalError(response.data.statusDescription || "Failed to add user role.");
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        setModalError("Unauthorized access. Your session may have expired or you lack permission to add users.");
        localStorage.removeItem("token");
        sessionStorage.removeItem("token");
        navigate(route.login);
      } else if (err.response?.status === 500) {
        if (groupName === "USER") {
          setModalError("Failed to add role. Ensure the department exists or contact support.");
        } else if (groupName === "DRIVER") {
          setModalError("Failed to add Driver role. Verify license details or contact support.");
        } else {
          setModalError("Server error occurred. Please try again or contact support.");
        }
      } else if (err.response?.status === 404) {
        setModalError("Group or department not found. Please check the selection.");
      } else {
        setModalError(err.response?.data?.statusDescription || "Error adding user role.");
      }
    } finally {
      setModalLoading(false);
    }
  };

  // Filter employees for table
  const filteredEmployees = employees.filter(
    (employee) =>
      employee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (employee.empNo && employee.empNo.toString().includes(searchTerm))
  );

  const totalDeptPages = deptTotal > 0 ? Math.ceil(deptTotal / PAGE_SIZE) : 1;

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-8">
                    <h4 className="page-title">Administration/Users/Add Users</h4>
                  </div>
                  <div className="col-4 text-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>
              <div className="card">
                <div className="card-body pb-0 pt-2">
                  <ul className="nav nav-tabs nav-tabs-bottom ">
                    {navItems1.map((item, index) =>
                      item.permission && checkPermission(item.permission) ? (
                        <li className="nav-item me-3" key={`nav-${index}`}>
                          <Link
                            to={item.link}
                            className={`nav-link px-0 ${index === 1 ? "active" : ""}`}
                          >
                            <i className={item.icon}></i> {item.label}
                          </Link>
                        </li>
                      ) : null
                    )}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Employee list */}
          <div className="row">
            <div className="col-xl-12">
              <div className="card">
                <div className="card-header justify-content-between align-items-center">
                  <div className="card-title">Employee List</div>
                  <div className="d-flex align-items-center">
                    <div className="col-6 me-2">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by Name or EmpNo"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                <div className="card-body">
                  {successMessage && (
                    <div className="alert alert-success" role="alert">
                      {successMessage}
                    </div>
                  )}
                  {loading ? (
                    <div className="text-center">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : error ? (
                    <div className="alert alert-danger" role="alert">
                      {error}
                    </div>
                  ) : filteredEmployees.length === 0 ? (
                    <p>No employees found.</p>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-borderless text-nowrap">
                        <thead>
                          <tr>
                            <th scope="col">Name</th>
                            <th scope="col">Employee No</th>
                            <th scope="col">Designation</th>
                            <th scope="col">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredEmployees.map((employee, index) => (
                            <tr key={`employee-${employee.id ?? employee.empNo ?? index}`}>
                              <td>
                                <span
                                  style={{ cursor: "pointer" }}
                                  onClick={() => handleNameClick(employee)}
                                >
                                  {employee.name}
                                </span>
                              </td>
                              <td>{employee.empNo || "N/A"}</td>
                              <td>{employee.designation}</td>
                              <td>
                                <span
                                  className={`badge bg-${
                                    employee.status === "ACTIVE"
                                      ? "success"
                                      : employee.status === "INACTIVE"
                                      ? "danger"
                                      : "warning"
                                  }`}
                                >
                                  {employee.status || "N/A"}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          {/* /Employee list */}
        </div>
      </div>

      {/* Add/Edit Role Modal */}
      <div className={`modal fade ${modalOpen ? "show d-block" : ""}`} tabIndex={-1} role="dialog">
        <div className="modal-dialog modal-lg" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Add/Edit User Role</h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => {
                  setModalOpen(false);
                  setSelectedUser(null);
                  setGroupName("");
                  setDriverData({ licenseType: "", licenseNumber: "", expireDate: "" });
                  setDeptHeadData([]);
                  setModalSearchTerm("");
                  setAvailableUsers([]);
                  setModalError(null);
                  setDeptPage(1);
                  setDeptSearch("");
                }}
              ></button>
            </div>
            <div className="modal-body">
              <div className="mb-3">
                {modalError && (
                  <div className="alert alert-danger mt-2" role="alert">
                    {modalError}
                  </div>
                )}
              </div>

              {modalLoading ? (
                <div className="text-center">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : availableUsers.length > 0 ? (
                <div className="mb-3">
                  <h6>Select User:</h6>
                  <ul className="list-group" style={{ maxHeight: "200px", overflowY: "auto" }}>
                    {availableUsers.map((user, index) => (
                      <li
                        key={`user-${user.id ?? user.empNo ?? index}`}
                        className={`list-group-item ${
                          selectedUser?.empNo === user.empNo ? "list-group-item-primary" : ""
                        }`}
                        onClick={() => handleSelectUser(user)}
                        style={{ cursor: "pointer" }}
                      >
                        {user.name} (EmpNo: {user.empNo})
                      </li>
                    ))}
                  </ul>
                  {selectedUser && (
                    <button className="btn btn-outline-secondary mt-2" onClick={handleClearSelection}>
                      Clear Selection
                    </button>
                  )}
                </div>
              ) : null}

              {selectedUser && (
                <>
                  <div className="mb-3">
                    <h6>
                      Selected User: {selectedUser.name} (EmpNo: {selectedUser.empNo})
                    </h6>
                  </div>

                  {/* Group selection (restricted) */}
                  <div className="mb-3">
                    <label className="form-label">Group</label>
                    <select
                      className="form-control"
                      value={groupName}
                      onChange={(e) => setGroupName(e.target.value)}
                    >
                      <option value="">Select Group</option>
                      {availableGroups
                        .filter((g) => allowedGroupNames.includes(g.name))
                        .map((group) => (
                          <option key={`group-${group.id}`} value={group.name}>
                            {group.name}
                          </option>
                        ))}
                    </select>
                    <small className="form-text text-muted d-block mt-1">
                      {currentUserGroup === "DEPARTMENT_HEAD"
                        ? "As a Department Head, you can assign only the USER role."
                        : "You can assign only HR_MANAGER or SERVICE_MANAGER roles."}
                    </small>
                  </div>

                  {/* Driver fields */}
                  {groupName === "DRIVER" && (
                    <>
                      <div className="mb-3">
                        <label className="form-label">License Type</label>
                        <div>
                          {["Heavy", "Normal"].map((type) => (
                            <div className="form-check form-check-inline" key={type}>
                              <input
                                className="form-check-input"
                                type="radio"
                                name="licenseType"
                                id={`license-${type}`}
                                value={type}
                                checked={driverData.licenseType === type}
                                onChange={(e) =>
                                  setDriverData({ ...driverData, licenseType: e.target.value })
                                }
                              />
                              <label className="form-check-label" htmlFor={`license-${type}`}>
                                {type}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="mb-3">
                        <label className="form-label">License Number</label>
                        <input
                          type="text"
                          className="form-control"
                          value={driverData.licenseNumber}
                          onChange={(e) =>
                            setDriverData({ ...driverData, licenseNumber: e.target.value })
                          }
                          placeholder="e.g., 123456789"
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Expiration Date</label>
                        <input
                          type="date"
                          className="form-control"
                          value={driverData.expireDate}
                          onChange={(e) =>
                            setDriverData({ ...driverData, expireDate: e.target.value })
                          }
                          placeholder="Select expiration date"
                        />
                      </div>
                    </>
                  )}

                  {/* Department selection (for USER & DEPARTMENT_HEAD only) */}
                  {(groupName === "USER" || groupName === "DEPARTMENT_HEAD") && (
                    <div className="mb-3">
                      <label className="form-label">Department</label>

                      {/* 🔍 Search bar for departments */}
                      <div className="mb-2">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search department by name"
                          value={deptSearch}
                          onChange={(e) => {
                            setDeptSearch(e.target.value);
                            setDeptPage(1); // reset to first page when searching
                          }}
                        />
                      </div>

                      {modalLoading ? (
                        <div className="text-center">
                          <div
                            className="spinner-border spinner-border-sm text-primary"
                            role="status"
                          >
                            <span className="visually-hidden">Loading departments...</span>
                          </div>
                        </div>
                      ) : availableDepartments.length === 0 ? (
                        <p className="text-muted">No departments available.</p>
                      ) : (
                        <>
                          <select
                            className="form-control"
                            value={deptHeadData[0]?.id?.toString() || ""}
                            onChange={(e) => {
                              const selectedDept = availableDepartments.find(
                                (dept) => dept.id === parseInt(e.target.value)
                              );
                              setDeptHeadData(selectedDept ? [selectedDept] : []);
                            }}
                          >
                            <option value="">Select Department</option>
                            {availableDepartments.map((dept) => (
                              <option key={`dept-${dept.id}`} value={dept.id}>
                                {dept.name}
                              </option>
                            ))}
                          </select>

                          {/* Pagination controls */}
                          <div className="d-flex justify-content-between align-items-center mt-2">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              disabled={deptPage <= 1}
                              onClick={() => setDeptPage((prev) => Math.max(prev - 1, 1))}
                            >
                              Previous
                            </button>
                            <small>
                              Page {deptPage} of {totalDeptPages}
                            </small>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              disabled={deptPage >= totalDeptPages}
                              onClick={() =>
                                setDeptPage((prev) =>
                                  prev < totalDeptPages ? prev + 1 : prev
                                )
                              }
                            >
                              Next
                            </button>
                          </div>
                        </>
                      )}
                      <small className="form-text text-muted">
                        Select one department for the user role.
                      </small>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  setModalOpen(false);
                  setSelectedUser(null);
                  setGroupName("");
                  setDriverData({ licenseType: "", licenseNumber: "", expireDate: "" });
                  setDeptHeadData([]);
                  setModalSearchTerm("");
                  setAvailableUsers([]);
                  setModalError(null);
                  setDeptPage(1);
                  setDeptSearch("");
                }}
              >
                Close
              </button>
              <button
                className="btn btn-success"
                onClick={handleAddUserRole}
                disabled={modalLoading || !checkPermission("ROLE_ADD_USERS")}
              >
                {modalLoading ? (
                  <span className="spinner-border spinner-border-sm me-2" role="status" />
                ) : (
                  "Add Role"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      {modalOpen && <div className="modal-backdrop fade show"></div>}
    </div>
  );
};

export default AddUsers;
